import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_ERROR_MESSAGE } from 'common/config';
import { ApiStatus, MessageType } from 'common/enums';
import { IErrorResponse } from 'common/models/api-service/error-response.model';

import {
    PERMISSION_CREATE_MESSAGE,
    PERMISSION_DELETE_MESSAGE,
    PERMISSION_EDIT_MESSAGE
} from '../config/user.config';
import { DeleteUserRoleApiPayload, UserPermission } from '../models/user.model';
import initialUserPermissionState from './user.state';
import { UserRoleInfo } from 'modules/admin-roles-management/models/role-management.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';

const userSlice = createSlice({
    name: 'user',
    initialState: initialUserPermissionState,
    reducers: {
        getUserList: (state) => {
            if (!state.userList) state.userList = {};
            state.userList.status = ApiStatus.LOADING;
        },
        getUserListSuccess: (state, { payload }: PayloadAction<Array<string>>) => {
            if (!state.userList) state.userList = {};
            state.userList.data = payload;
            state.userList.status = ApiStatus.SUCCESS;
        },
        getUserListError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.userList) state.userList = {};
            state.userList.error = payload;
            state.userList.status = ApiStatus.ERROR;
        },
        getRolesList: (state) => {
            if (!state.roleList) state.roleList = {};
            state.roleList.status = ApiStatus.LOADING;
        },
        getRolesListSuccess: (state, { payload }: PayloadAction<Array<string>>) => {
            if (!state.roleList) state.roleList = {};
            state.roleList.data = payload;
            state.roleList.status = ApiStatus.SUCCESS;
        },
        getRolesListError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.roleList) state.roleList = {};
            state.roleList.error = payload;
            state.roleList.status = ApiStatus.ERROR;
        },
        getPermissionList: (state, { payload }: PayloadAction<any>) => {
            if (!state.permissionList) state.permissionList = {};
            state.permissionList.status = ApiStatus.LOADING;
        },
        getPermissionListSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<Array<UserPermission>>>
        ) => {
            if (!state.permissionList) state.permissionList = {};
            state.permissionList.data = payload.data;
            state.permissionList.status = ApiStatus.SUCCESS;
            state.permissionList.totalCount = payload.pagination.totalCount; //ok
            state.permissionList.pageSize = payload.pagination.pageSize; //ok
            state.permissionList.totalPages = payload.pagination.totalPages; //ok
            state.permissionList.currentPage = payload.pagination.currentPage; //ok
        },
        getPermissionListError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.permissionList) state.permissionList = {};
            state.permissionList.error = payload;
            state.permissionList.status = ApiStatus.ERROR;
        },
        editPermission: (state, { payload }: PayloadAction<UserRoleInfo>) => {
            state.permissionEditData = payload;
            state.isAdminModalVisible = true;
        },
        deletePermission: (
            state,
            {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                payload
            }: PayloadAction<{
                data: DeleteUserRoleApiPayload;
                onSuccess: (param: boolean) => void;
            }>
        ) => {
            if (!state.permissionDeleteStatus) state.permissionDeleteStatus = {};
            state.permissionDeleteStatus.status = ApiStatus.LOADING;
        },
        deletePermissionSuccess: (state) => {
            if (!state.permissionDeleteStatus) state.permissionDeleteStatus = {};
            state.permissionDeleteStatus.status = ApiStatus.SUCCESS;
            state.permissionDeleteStatus.message = PERMISSION_DELETE_MESSAGE;
        },
        deletePermissionError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.permissionDeleteStatus) state.permissionDeleteStatus = {};
            state.permissionDeleteStatus.error = payload;
            state.permissionDeleteStatus.status = ApiStatus.ERROR;
            state.permissionDeleteStatus.message = DEFAULT_ERROR_MESSAGE;
        },
        assignRole: (state, { payload }: PayloadAction<any>) => {
            if (!state.roleAssignStatus) state.roleAssignStatus = {};
            state.roleAssignStatus.status = ApiStatus.LOADING;
        },
        assignRoleSuccess: (state) => {
            if (!state.roleAssignStatus) state.roleAssignStatus = {};
            state.roleAssignStatus.status = ApiStatus.SUCCESS;
            state.roleAssignStatus.message = PERMISSION_CREATE_MESSAGE;
        },
        assignRoleError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.roleAssignStatus) state.roleAssignStatus = {};
            state.roleAssignStatus.error = payload;
            state.roleAssignStatus.status = ApiStatus.ERROR;
        },
        editRole: (state, { payload }: PayloadAction<any>) => {
            if (!state.roleEditStatus) state.roleEditStatus = {};
            state.roleEditStatus.status = ApiStatus.LOADING;
        },
        editRoleSuccess: (state) => {
            if (!state.roleEditStatus) state.roleEditStatus = {};
            state.roleEditStatus.status = ApiStatus.SUCCESS;
            state.roleAssignStatus.message = PERMISSION_EDIT_MESSAGE;
        },
        editRoleError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.roleEditStatus) state.roleEditStatus = {};
            state.roleEditStatus.error = payload;
            state.roleEditStatus.status = ApiStatus.ERROR;
        },
        setPermissionDeleteData: (state, { payload }: PayloadAction<any>) => {
            state.dataForDelete = payload;
        },
        resetPermissionEditData: (state) => {
            state.permissionEditData = undefined;
        },
        resetPermissionMessageStatus: (state) => {
            const messageStatus = {
                message: '',
                type: MessageType.Default
            };
            state.messageStatus = messageStatus;
        },
        setUserSize: (state, { payload }: PayloadAction<number>) => {
            state.userPerPage = payload;
        },
        setAddUserModalVisibility: (state, { payload }: PayloadAction<boolean>) => {
            state.isAdminModalVisible = payload;
        },
        getUserRoles: (state) => {
            if (!state.userRoles) state.userRoles = {};
            state.userRoles.status = ApiStatus.LOADING;
        },
        getUserRolesSuccess: (state, { payload }: PayloadAction<Array<string>>) => {
            if (!state.userRoles) state.userRoles = {};
            state.userRoles.data = payload;
            state.userRoles.status = ApiStatus.SUCCESS;
        },
        getUserRolesError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.userRoles) state.userRoles = {};
            state.userRoles.error = payload;
            state.userRoles.status = ApiStatus.ERROR;
        }
    }
});

export default userSlice.reducer;
export const userActions = userSlice.actions;
